/**
 * Created by Ning Jiang on 3/20/2017.
 */

import {SpacierArcadeServerDataParser} from "../SpacierArcadeServerDataParser";
import {ISpacierArcadeGameData} from "./ISpacierArcadeServerData";
import {SpacierArcadeGameMode} from "../../gamemode/SpacierArcadeGameMode";
import {IWinRatioConfig} from "@nolimitcity/slot-game/bin/core/winpresentation/WinRatio";
import {SpacierArcadeGameConfig} from "../../SpacierArcadeGameConfig";
import {SpinParsedGameData} from "@nolimitcity/slot-game/bin/game/reel/spinreel/SpinParsedServerData";

export class SpacierArcadeParsedGameData extends SpinParsedGameData {

    private readonly _nudgeForNextSpin: boolean[];
    private readonly _extraSymbolsOnTop: string[][];
    private readonly _payoutMultiplier: number;
    private readonly _payoutMultiplierNext: number;
    private readonly _numberOfWinWays: number;
    private readonly _accumulatedRoundWin: string;
    private readonly _numberOfRespinPlayed: number;
    private readonly _nbrOfRowsNextSpin: number;
    private readonly _nbrOfRows: number;
    private readonly _overlaySymbolsAfterSpin:string[][];
    private readonly _overlaySymbolsBeforeSpin:string[][];
    private readonly _newOverlaySymbols:string[][];
    private readonly _overlayWildsMultipliers:number[][];
    private readonly _overlayWildsMultipliersNextSpin:number[][];
    private readonly _totalSpinWinnings:number;
    private readonly _currentTotalWinRatio:number;
    private readonly _currentTotalWinRatioConfig:IWinRatioConfig | null;
    private readonly _isSABigWin:boolean;
    private readonly _wildPositions:number[];
    private readonly _totalWinWaysWon:number;
    private readonly _fsWildOnReelExcitement: boolean[];
    private readonly _fsWildPosition: number[];
    private readonly _fsWildPositions: number[][];
    private readonly _spinIntensity: number;

    constructor(rawData: ISpacierArcadeGameData, dataParser: SpacierArcadeServerDataParser) {
        super(rawData, dataParser);
        this._totalWinWaysWon = rawData.totalWinWaysWon;
        this._extraSymbolsOnTop = dataParser.parseExtraSymbolsOnTop(rawData);
        this._payoutMultiplier = dataParser.parsePayoutMultiplier(rawData);
        this._payoutMultiplierNext = rawData.payoutMultiplierNext;
        this._numberOfRespinPlayed = dataParser.parseNumberOfRespinPlayed(rawData);
        this._accumulatedRoundWin = dataParser.parseAccumulatedRoundWin(rawData);
        this._numberOfWinWays = dataParser.parsePossibleWinWays(rawData);
        this._nbrOfRowsNextSpin = dataParser.parseRowsNextSpinCount(rawData);
        this._nbrOfRows = dataParser.parseRowsCurrentSpinCount(rawData);
        this._overlaySymbolsBeforeSpin = rawData.overlaySymbolsBeforeSpin;
        this._overlaySymbolsAfterSpin = rawData.overlaySymbolsAfterSpin;
        this._newOverlaySymbols = rawData.newOverlaySymbols;
        this._overlayWildsMultipliers = rawData.overlayWildsMultipliers;
        this._overlayWildsMultipliersNextSpin = rawData.overlayWildsMultipliersNextSpin;
        this._totalSpinWinnings = dataParser.parseTotalSpinWinnings(rawData);

        this._currentTotalWinRatio = this._totalSpinWinnings / dataParser.parsePlayedBetValue(rawData);
        this._isSABigWin = this._currentTotalWinRatio >= SpacierArcadeGameConfig.instance.WIN_RATIOS.bigWin[0].ratio;
        this._currentTotalWinRatioConfig = this.getWinRatioConfig(this._currentTotalWinRatio);
        this._wildPositions = rawData.wildPositions;
        this._fsWildOnReelExcitement = rawData.fsWildOnReelExcitement;
        this._fsWildPosition = rawData.fsWildPosition;
        this._fsWildPositions = rawData.fsWildPositions;
        this._spinIntensity = rawData.spinIntensity;
    }

    public get spinIntensity():number{
        return this._spinIntensity;
    }

    public get fsWildPosition():number[]{
        return this._fsWildPosition;
    }

    public get fsWildPositions():number[][]{
        return this._fsWildPositions;
    }

    public get fsWildOnReelExcitement():boolean[] {
        return this._fsWildOnReelExcitement;
    }

    public get totalWinWaysWon():number {
        return this._totalWinWaysWon;
    }

    public get wildPositions():number[]{
        return this._wildPositions;
    }

    public get isBigWin():boolean {
        return this._isSABigWin;
    }

    public get isNormalBigWin():boolean {
        return this.currentWinRatio >= SpacierArcadeGameConfig.instance.WIN_RATIOS.bigWin[0].ratio;
    }

    public get currentTotalWinRatio():number {
        return this._currentTotalWinRatio;
    }

    public get currentTotalWinRatioConfig():IWinRatioConfig | null {
        return this._currentTotalWinRatioConfig;
    }

    public get totalSpinWinnigs():number {
        return this._totalSpinWinnings;
    }

    public get overlayWildsMultipliers():number[][]{
        return this._overlayWildsMultipliers;
    }

    public get overlayWildsMultipliersNextSpin():number[][]{
        return this._overlayWildsMultipliersNextSpin;
    }

    public get newOverlaySymbols():string[][]{
        return this._newOverlaySymbols;
    }

    public get overlaySymbolsBeforeSpin():string[][]{
        return this._overlaySymbolsBeforeSpin;
    }

    public get overlaySymbolsAfterSpin():string[][]{
        return this._overlaySymbolsAfterSpin;
    }

    public get extraSymbolsOnTop(): string[][] {
        return this._extraSymbolsOnTop;
    }

    public get payoutMultiplier(): number {
        return this._payoutMultiplier;
    }

    public get payoutMultiplierNext(): number {
        return this._payoutMultiplierNext;
    }

    public get numberOfRespinPlayed(): number {
        return this._numberOfRespinPlayed;
    }

    public get numberOfNextActiveRows(): number {
        return this._nbrOfRowsNextSpin;
    }

    public get numberOfActiveRows(): number {
        return this._nbrOfRows;
    }

    public get accumulatedRoundWin(): string {
        return this._accumulatedRoundWin;
    }

    public get numberOfWinWaysNextSpin(): number {
        return this._numberOfWinWays;
    }

    protected isFeatureTotalWin(): boolean {
        return this.mode === SpacierArcadeGameMode.RESPIN || this.mode === SpacierArcadeGameMode.FREESPIN_RESPIN;
    }

    protected isNextFeatureTotalWin(): boolean {
        return (this.mode === SpacierArcadeGameMode.RESPIN && this.nextMode === SpacierArcadeGameMode.RESPIN) || (this.mode === SpacierArcadeGameMode.FREESPIN_RESPIN && this.nextMode === SpacierArcadeGameMode.FREESPIN_RESPIN);
    }


    private getWinRatioConfig(winRatio:number):IWinRatioConfig | null {
        let winLevels:IWinRatioConfig[] = SpacierArcadeGameConfig.instance.WIN_RATIOS.normalWin.concat();
        winLevels = winLevels.concat(SpacierArcadeGameConfig.instance.WIN_RATIOS.bigWin);

        for(let i:number = winLevels.length - 1; i >= 0; i--) {
            const winLevel = winLevels[i];
            if(winRatio >= winLevel.ratio) {
                return winLevel;
            }
        }
        return null;
    }
}